import React from 'react';
import {FilePond, registerPlugin} from "react-filepond";
import 'filepond/dist/filepond.min.css';
import {useSelector} from "react-redux";
import FilePondPluginPdfPreview from "filepond-plugin-pdf-preview";
import FilePondPluginFileValidateType from "filepond-plugin-file-validate-type";

registerPlugin(FilePondPluginPdfPreview, FilePondPluginFileValidateType);

/**
 * Multi-file variant of FileUploader2 for the RFA "multiple uploads" flow.
 *
 * Each accepted PDF is uploaded to `api` (upload-pdf2) as a temporary file.
 * The resulting temporary file ids are reported through onChange so the caller
 * can finalise them (finish-upload-pdf) once the manager confirms. Removing a
 * still-pending file reverts the temporary upload on the server.
 *
 * `resetKey` — change it (e.g. increment) to force-remount and clear the pond
 * after the pending files have been finalised.
 */
const FileUploaderMultiple = ({api, onChange, resetKey}) => {
    const idToken = useSelector(state => state.auth.idToken);
    // Maps a FilePond item id -> server temporary file id.
    const mapRef = React.useRef({});

    const emit = () => onChange(Object.values(mapRef.current));

    const serverConfig = {
        revert: null,
        load: () => {},
        process: (fieldName, file, metadata, load, error, progress, abort) => {
            const formData = new FormData();
            formData.append(fieldName, file, file.name);

            const request = new XMLHttpRequest();
            request.open('POST', api);
            request.setRequestHeader('Authorization', idToken);
            request.upload.onprogress = (e) => progress(e.lengthComputable, e.loaded, e.total);
            request.onload = function () {
                if (request.status >= 200 && request.status < 300) {
                    // Expose only the numeric id as the FilePond serverId so it
                    // is available on the item in onprocessfile / onremovefile.
                    load(String(JSON.parse(request.responseText).file_id));
                } else {
                    error(request.response);
                }
            };
            request.send(formData);

            return {abort: () => { request.abort(); abort(); }};
        },
    };

    const handleProcessFile = (error, file) => {
        if (!error && file.serverId) {
            mapRef.current[file.id] = parseInt(file.serverId, 10);
            emit();
        }
    };

    const handleRemoveFile = (error, file) => {
        const tempFileId = mapRef.current[file.id];
        if (tempFileId) {
            const request = new XMLHttpRequest();
            request.open('DELETE', `${api}/${tempFileId}`);
            request.setRequestHeader('Authorization', idToken);
            request.send();
            delete mapRef.current[file.id];
            emit();
        }
    };

    return (
        <FilePond
            key={resetKey}
            acceptedFileTypes={['application/pdf']}
            allowMultiple={true}
            server={serverConfig}
            stylePanelLayout="compact"
            name="fileUpload1"
            onprocessfile={handleProcessFile}
            onremovefile={handleRemoveFile}
            credits={{}}
            labelFileProcessingError={(err) => err.body}
        />
    );
};

export default FileUploaderMultiple;
